import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import UpgradeModal from "@/components/UpgradeModal";
import { Package, Truck, Clock, Scales, Cube, Plus, Sparkle, Crown, Lightning } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";

const KPI = ({ label, value, icon: Icon, accent = "#2563EB" }) => (
  <div className="bg-white border border-slate-200 p-4 sm:p-5 flex items-start gap-3 sm:gap-4 rounded-sm" data-testid={`kpi-${label}`}>
    <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-sm shrink-0" style={{ backgroundColor: accent + "15", color: accent }}>
      <Icon size={20} weight="duotone" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-500 font-semibold truncate">{label}</div>
      <div className="font-display text-2xl sm:text-3xl font-bold mt-1 tracking-tight data-cell">{value}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [quota, setQuota] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/workspace/quota").then((r) => setQuota(r.data)).catch(() => {
      api.get("/auth/quota").then((r) => setQuota(r.data)).catch(() => {});
    });
  }, []);

  const pct = quota && quota.limit ? Math.round((quota.used / quota.limit) * 100) : 0;
  const atLimit = quota && !quota.unlimited && quota.remaining === 0;
  const showStarterQuota = quota && ["free", "trial", "expired"].includes(String(quota.subscription || "").toLowerCase());

  return (
    <div>
      <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle")}>
        <Link data-testid="cta-new-plan" to="/plans/new" className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white px-4 py-2 text-sm font-semibold rounded-sm">
          <Plus size={16} weight="bold" /> {t("dashboard.new_plan")}
        </Link>
      </PageHeader>

      <div className="p-4 sm:p-8 space-y-4 sm:space-y-6">
        {showStarterQuota && (
          <div data-testid="quota-banner" className={`rounded-sm border-l-4 ${atLimit ? "border-red-500 bg-red-50" : "border-amber-500 bg-amber-50"} p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3`}>
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={`w-10 h-10 shrink-0 rounded-sm flex items-center justify-center ${atLimit ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                {atLimit ? <Sparkle size={20} weight="fill" /> : <Lightning size={20} weight="duotone" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs sm:text-sm font-semibold uppercase tracking-wider ${atLimit ? "text-red-800" : "text-amber-900"}`}>
                  {atLimit ? t("final.dashboard.quota_full") : t("final.dashboard.free_quota")}
                </div>
                <div className="mt-0.5 text-sm sm:text-base text-slate-800">
                  Bu ay <b data-testid="quota-used">{quota.used}</b> / <b data-testid="quota-limit">{quota.limit}</b> plan kullanıldı · Sınırsız plan için Pro'ya geçin.
                </div>
                <div className="mt-2 h-2 bg-white/60 rounded-full overflow-hidden max-w-md">
                  <div className={`h-full transition-all ${atLimit ? "bg-red-500" : "bg-amber-500"}`} data-testid="quota-progress-bar" style={{ width: `${Math.max(6, Math.min(100, pct))}%` }} />
                </div>
              </div>
            </div>
            {!quota.is_team_member && <button onClick={() => setUpgradeOpen(true)} data-testid="quota-upgrade-btn" className="inline-flex items-center gap-1.5 shrink-0 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 rounded-sm">
              <Crown size={14} weight="fill" /> {t("final.dashboard.upgrade")}
            </button>}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KPI label={t("dashboard.today_plan")} value={stats?.today_plans_count ?? "—"} icon={Clock} accent="#2563EB" />
          <KPI label={t("dashboard.today_pallet")} value={stats?.today_pallets ?? "—"} icon={Truck} accent="#16A34A" />
          <KPI label={t("dashboard.total_kg")} value={stats?.total_kg?.toFixed?.(1) ?? "—"} icon={Scales} accent="#DC2626" />
          <KPI label={t("dashboard.total_boxes")} value={stats?.total_boxes ?? "—"} icon={Cube} accent="#F59E0B" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="border-b border-slate-200 px-4 sm:px-5 py-3 flex items-center justify-between">
              <h3 className="font-display font-bold text-sm uppercase tracking-wider">{t("dashboard.recent_plans")}</h3>
              <Link to="/plans" className="text-xs text-[#2563EB] hover:underline">{t("dashboard.view_all")}</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table w-full min-w-[560px]">
                <thead><tr><th>Plan No</th><th>{t("dashboard.shipment")}</th><th>{t("dashboard.country")}</th><th>{t("dashboard.pallet")}</th><th>{t("dashboard.status")}</th></tr></thead>
                <tbody>
                  {(stats?.recent_plans || []).map((p) => (
                    <tr key={p.id} data-testid={`recent-${p.plan_no}`}>
                      <td className="font-mono text-xs"><Link to={`/plans/${p.id}`} className="text-[#2563EB] hover:underline">{p.plan_no}</Link></td>
                      <td>{p.shipment_name}</td><td>{p.country}</td><td className="data-cell">{p.pallets?.length || 0}</td>
                      <td><span className={`text-[10px] uppercase px-2 py-0.5 font-semibold ${p.status === "approved" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>{p.status === "approved" ? t("dashboard.approved") : t("dashboard.waiting")}</span></td>
                    </tr>
                  ))}
                  {(!stats?.recent_plans || stats.recent_plans.length === 0) && <tr><td colSpan={5} className="text-center text-slate-400 py-8">{t("dashboard.no_plan")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-[#0F172A] text-white p-5 sm:p-6 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{t("dashboard.pending_plans")}</div>
            <div className="font-display text-5xl sm:text-6xl font-extrabold mt-3">{stats?.pending_count ?? "—"}</div>
            <div className="text-sm text-slate-400 mt-2">{t("dashboard.pending_desc")}</div>
            <div className="mt-5 border-t border-slate-800 pt-4 text-xs">
              <div className="flex justify-between py-1"><span className="text-slate-400">{t("dashboard.total_plans")}</span><b>{stats?.total_plans ?? "—"}</b></div>
              <div className="flex justify-between py-1"><span className="text-slate-400">{t("dashboard.total_pallet")}</span><b>{stats?.total_pallets ?? "—"}</b></div>
              <div className="flex justify-between py-1"><span className="text-slate-400">{t("dashboard.total_kg")}</span><b>{stats?.total_kg?.toFixed?.(1) ?? "—"}</b></div>
            </div>
          </div>
        </div>
      </div>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} usage={quota ? { limit: quota.limit, used: quota.used, subscription: quota.subscription } : null} />
    </div>
  );
}
